import _ from 'lodash'
import { Palette } from 'lucide-react'
import { HorizontalCarouselNav } from '@/components/casino/lobby/carousel/HorizontalCarouselNav'
import { ProviderLobbyCard } from '@/components/casino/lobby/ProviderCard'
import { ProviderCardSkeleton } from '@/components/casino/lobby/ProviderCardSkeleton'
import { CASINO_PROVIDERS_PER_CAROUSEL, CASINO_PROVIDERS_PER_PAGE, CASINO_TYPE_PROVIDERS } from '@/config/casino'
import { useHorizontalCarousel } from '@/hooks/use-horizontal-carousel'
import { useGetCasinoProvidersQuery } from '@/store/api/casinoApi'
import { useAppDispatch } from '@/store/hooks'
import { setSelectedCasinoType } from '@/store/slices/casinoSlice'

export function CasinoProvidersSection() {
	const dispatch = useAppDispatch()
	const { data, isLoading, isError } = useGetCasinoProvidersQuery({
		page: 1,
		per_page: CASINO_PROVIDERS_PER_PAGE,
	})

	const providers = data?.data ?? []
	const hasCarousel = !isLoading && !isError && !_.isEmpty(providers)
	const { scrollRef, canScrollPrev, canScrollNext, handleScroll } = useHorizontalCarousel(providers)

	return (
		<section className='flex flex-col gap-4'>
			<div className='flex items-center justify-between gap-4'>
				<div className='flex items-center gap-3'>
					<Palette className='h-7 w-7 text-subtle-foreground' />
					<h2 className='text-2xl font-semibold tracking-[-0.12px] text-foreground'>Providers</h2>
				</div>
				{hasCarousel ? (
					<div className='flex items-center gap-3'>
						<button
							type='button'
							onClick={() => dispatch(setSelectedCasinoType(CASINO_TYPE_PROVIDERS))}
							className='text-sm font-medium tracking-[-0.21px] text-muted-foreground transition-colors hover:text-foreground'
						>
							View all
						</button>
						<HorizontalCarouselNav canScrollPrev={canScrollPrev} canScrollNext={canScrollNext} onScroll={handleScroll} />
					</div>
				) : null}
			</div>

			{isError ? <p className='text-sm text-muted-foreground'>Unable to load game providers right now.</p> : null}

			{isLoading ? (
				<div className='flex overflow-x-auto pb-8 pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'>
					{_.times(CASINO_PROVIDERS_PER_CAROUSEL, (index) => (
						<div
							key={`provider-skeleton-${index}`}
							className='w-1/2 min-w-[50%] shrink-0 pr-5 sm:w-1/3 sm:min-w-[33.333%] md:w-1/4 md:min-w-[25%] lg:w-1/6 lg:min-w-[16.666%]'
						>
							<ProviderCardSkeleton variant='lobby' className='w-full' />
						</div>
					))}
				</div>
			) : null}

			{!isLoading && !isError && _.isEmpty(providers) ? <p className='text-sm text-muted-foreground'>No game providers are available right now.</p> : null}

			{hasCarousel ? (
				<div className='min-w-0'>
					<div ref={scrollRef} className='flex overflow-x-auto overflow-y-visible scroll-smooth pb-8 pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'>
						{_.map(providers, (provider) => (
							<div key={provider.id} className='w-1/2 min-w-[50%] shrink-0 pr-5 sm:w-1/3 sm:min-w-[33.333%] md:w-1/4 md:min-w-[25%] lg:w-1/6 lg:min-w-[16.666%]'>
								<ProviderLobbyCard provider={provider} className='w-full' />
							</div>
						))}
					</div>
				</div>
			) : null}
		</section>
	)
}
